import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  approveTrendDiscoveryCandidate,
  archiveTrendArticlesBefore,
  backfillTrendImages,
  cancelTrendRun,
  createTrendResetSnapshot,
  createTrendDiscoveryQuery,
  createTrendDiscoverySourceDraft,
  createTrendSource,
  deleteTrendSource,
  downloadTrendResetSnapshot,
  fetchTrendDiscoveryCandidates,
  fetchTrendDiscoveryQueries,
  fetchTrendDiscoveryStatus,
  fetchTrendPilotRunPresets,
  fetchTrendResetPreview,
  fetchRecommendedTrendSources,
  fetchBrandRadarHits,
  fetchTrendRuns,
  fetchTrendSettings,
  fetchTrendSources,
  fetchTrendTestSets,
  hardResetTrendData,
  importTrendDiscoveryCandidate,
  importRecommendedTrendSources,
  planTrendRun,
  quarantineTrendDiscoveryCandidate,
  rejectTrendDiscoveryCandidate,
  runTrendPipelineNow,
  runTrendDiscoveryNow,
  saveTrendSettings,
  startPlannedTrendRun,
  updateBrandRadarHit,
  updateTrendDiscoveryQuery,
  updateTrendSource,
} from '../service';
import { extractTrendUiErrorMessage } from '../errorUtils';
import type {
  RecommendedTrendSource,
  TrendBrandRadarHit,
  TrendCategoryRunMode,
  TrendDiscoveryCandidate,
  TrendDiscoveryQuery,
  TrendDiscoveryRunState,
  TrendHardResetResult,
  TrendModuleSettings,
  TrendPipelineRunInputMode,
  TrendPipelineRunProfile,
  TrendPipelineRun,
  TrendPilotRunPreset,
  TrendRunPlan,
  TrendRunRequest,
  TrendResetPreview,
  TrendResetSnapshotResult,
  TrendTestSetSummary,
  TrendSource,
} from '../types';
import {
  ACTIVE_RUN_STATUSES,
  DISCOVERY_CONTRACT_FALLBACK,
  EMPTY_DISCOVERY_QUERY,
  EMPTY_SOURCE,
  STALLED_RUN_HEARTBEAT_THRESHOLD_MS,
  STALLED_RUN_PROGRESS_THRESHOLD_MS,
} from './adminConstants';
import type { MultiValueDrafts } from './adminTypes';
import {
  buildMultiValueDrafts,
  formatRelativeRunAge,
  getDiscoveryQueriesForPreset,
  getDiscoverySummaryNumber,
  getRunCategoryModeLabel,
  getSourceTestSources,
  getTimestampAgeMs,
  joinMultiValue,
  mapFriendlyActionError,
  mapFriendlyRecommendedError,
  splitMultiValue,
} from './adminHelpers';

export function useTrendAdminController(
  canAdmin: boolean,
  canReview = false,
  canManageSources = true,
  navigateToView?: (view: 'overview' | 'editorial') => void,
) {
  const [sources, setSources] = useState<TrendSource[]>([]);
  const [recommendedSources, setRecommendedSources] = useState<RecommendedTrendSource[]>([]);
  const [testSets, setTestSets] = useState<TrendTestSetSummary[]>([]);
  const [pilotPresets, setPilotPresets] = useState<TrendPilotRunPreset[]>([]);
  const [pilotPlan, setPilotPlan] = useState<TrendRunPlan | null>(null);
  const [pilotPresetError, setPilotPresetError] = useState<string | null>(null);
  const [discoveryCandidates, setDiscoveryCandidates] = useState<TrendDiscoveryCandidate[]>([]);
  const [discoveryQueries, setDiscoveryQueries] = useState<TrendDiscoveryQuery[]>([]);
  const [brandRadarHits, setBrandRadarHits] = useState<TrendBrandRadarHit[]>([]);
  const [brandRadarQueriesDraft, setBrandRadarQueriesDraft] = useState('Fontana\nFontana Food\nFontana olivolja\nFontana feta');
  const [showBrandRadarFalsePositives, setShowBrandRadarFalsePositives] = useState(false);
  const [settings, setSettings] = useState<TrendModuleSettings | null>(null);
  const [multiValueDrafts, setMultiValueDrafts] = useState<MultiValueDrafts>(buildMultiValueDrafts(null));
  const [runs, setRuns] = useState<TrendPipelineRun[]>([]);
  const [discoveryRun, setDiscoveryRun] = useState<TrendDiscoveryRunState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testSetsError, setTestSetsError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [recommendedError, setRecommendedError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [discoveryQueryDialogOpen, setDiscoveryQueryDialogOpen] = useState(false);
  const [draftSource, setDraftSource] = useState<Partial<TrendSource>>(EMPTY_SOURCE);
  const [draftDiscoveryQuery, setDraftDiscoveryQuery] = useState<Partial<TrendDiscoveryQuery>>(EMPTY_DISCOVERY_QUERY);
  const [focusTagsDraft, setFocusTagsDraft] = useState('');
  const [busyAction, setBusyAction] = useState<
    'run' | 'cancel' | 'import' | 'saveSettings' | 'saveSource' | 'saveDiscoveryQuery'
    | 'discoveryRun' | 'discoveryReview' | 'imageBackfill' | 'archive'
    | 'resetPreview' | 'resetSnapshot' | 'hardReset'
    | 'pilotPlan' | 'pilotStart' | 'brandRadarRun' | 'brandRadarReview'
    | null
  >(null);
  const [activeTab, setActiveTab] = useState(0);
  const [runProfile, setRunProfile] = useState<TrendPipelineRunProfile>('full');
  const [runInputMode, setRunInputMode] = useState<TrendPipelineRunInputMode>('live_sources');
  const [runCategoryRunMode, setRunCategoryRunMode] = useState<TrendCategoryRunMode>('full');
  const [runFixtureKey, setRunFixtureKey] = useState('');
  const [runSourceItemIdsDraft, setRunSourceItemIdsDraft] = useState('');
  const [sourceTestSourceIds, setSourceTestSourceIds] = useState<string[]>([]);
  const [pilotSourceTestSourceId, setPilotSourceTestSourceId] = useState('');
  const [pilotDiscoveryQueryId, setPilotDiscoveryQueryId] = useState('');
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetPreview, setResetPreview] = useState<TrendResetPreview | null>(null);
  const [resetSnapshot, setResetSnapshot] = useState<TrendResetSnapshotResult | null>(null);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetRestartPipeline, setResetRestartPipeline] = useState(false);
  const [resetSummary, setResetSummary] = useState<TrendHardResetResult | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setTestSetsError(null);
    setRecommendedError(null);
    setPilotPresetError(null);

    const [sourceResult, recommendedResult, testSetsResult, pilotPresetResult, settingsResult, runsResult, discoveryResult, discoveryStatusResult, discoveryQueriesResult, brandRadarResult] = await Promise.allSettled([
      fetchTrendSources(),
      fetchRecommendedTrendSources(),
      fetchTrendTestSets(),
      fetchTrendPilotRunPresets(),
      fetchTrendSettings(),
      fetchTrendRuns(12),
      fetchTrendDiscoveryCandidates({ limit: 30 }),
      fetchTrendDiscoveryStatus(),
      fetchTrendDiscoveryQueries({ limit: 100 }),
      fetchBrandRadarHits({ includeFalsePositives: showBrandRadarFalsePositives, limit: 40 }),
    ]);

    if (sourceResult.status === 'fulfilled') setSources(sourceResult.value);
    if (recommendedResult.status === 'fulfilled') {
      setRecommendedSources(recommendedResult.value);
    } else {
      setRecommendedSources([]);
      setRecommendedError(mapFriendlyRecommendedError((recommendedResult as PromiseRejectedResult).reason));
    }
    if (testSetsResult.status === 'fulfilled') {
      setTestSets(testSetsResult.value);
    } else {
      setTestSets([]);
      setTestSetsError(extractTrendUiErrorMessage(
        (testSetsResult as PromiseRejectedResult).reason,
        'Testdata kunde inte laddas från backend. Starta om backend och ladda om sidan.',
      ));
    }
    if (pilotPresetResult.status === 'fulfilled') {
      setPilotPresets(pilotPresetResult.value);
    } else {
      setPilotPresets([]);
      setPilotPresetError(extractTrendUiErrorMessage(
        (pilotPresetResult as PromiseRejectedResult).reason,
        'Förvalda testkörningar kunde inte laddas från backend.',
      ));
    }
    if (settingsResult.status === 'fulfilled') {
      setSettings(settingsResult.value);
      setMultiValueDrafts(buildMultiValueDrafts(settingsResult.value));
    }
    if (runsResult.status === 'fulfilled') setRuns(runsResult.value);
    if (discoveryResult.status === 'fulfilled') setDiscoveryCandidates(discoveryResult.value.items);
    else setDiscoveryCandidates([]);
    if (discoveryStatusResult.status === 'fulfilled') setDiscoveryRun(discoveryStatusResult.value);
    if (discoveryQueriesResult.status === 'fulfilled') setDiscoveryQueries(discoveryQueriesResult.value);
    else setDiscoveryQueries([]);
    if (brandRadarResult.status === 'fulfilled') setBrandRadarHits(brandRadarResult.value);
    else setBrandRadarHits([]);

    const criticalErrors = [sourceResult, testSetsResult, settingsResult, runsResult, discoveryQueriesResult, brandRadarResult]
      .filter((r) => r.status === 'rejected')
      .map((r) => (r as PromiseRejectedResult).reason);
    if (criticalErrors.length > 0) {
      const first: any = criticalErrors[0];
      setError(extractTrendUiErrorMessage(first, 'Kunde inte ladda trendadministrationen.'));
    }
  }, [showBrandRadarFalsePositives]);

  useEffect(() => { void load(); }, [load]);

  const activeRun = runs.find((run) => ACTIVE_RUN_STATUSES.has(run.status)) || null;
  const latestRun = runs[0] || null;
  const visibleRun = activeRun || latestRun;
  const hasActiveRun = !!activeRun;
  const hasActiveDiscoveryRun = discoveryRun?.status === 'running';
  const latestRunUpdatedLabel = formatRelativeRunAge(visibleRun?.updatedAt);
  const latestRunHeartbeatLabel = formatRelativeRunAge(visibleRun?.heartbeatAt);
  const latestRunPhaseStartedLabel = formatRelativeRunAge(visibleRun?.phaseStartedAt);
  const latestRunAgeMs = getTimestampAgeMs(visibleRun?.updatedAt);
  const latestRunHeartbeatAgeMs = getTimestampAgeMs(visibleRun?.heartbeatAt);
  const latestRunUsesLegacyTracking = !!visibleRun && hasActiveRun && !visibleRun?.heartbeatAt;
  const latestRunHeartbeatStale = !!visibleRun
    && hasActiveRun
    && !latestRunUsesLegacyTracking
    && typeof latestRunHeartbeatAgeMs === 'number'
    && Number.isFinite(latestRunHeartbeatAgeMs)
    && latestRunHeartbeatAgeMs > STALLED_RUN_HEARTBEAT_THRESHOLD_MS;
  const latestRunSlowButAlive = !!visibleRun
    && hasActiveRun
    && !latestRunUsesLegacyTracking
    && !latestRunHeartbeatStale
    && typeof latestRunAgeMs === 'number'
    && Number.isFinite(latestRunAgeMs)
    && latestRunAgeMs > STALLED_RUN_PROGRESS_THRESHOLD_MS;
  const latestRunLooksStuck = !!visibleRun
    && hasActiveRun
    && (
      latestRunHeartbeatStale
      || (
        latestRunUsesLegacyTracking
        && typeof latestRunAgeMs === 'number'
        && Number.isFinite(latestRunAgeMs)
        && latestRunAgeMs > STALLED_RUN_PROGRESS_THRESHOLD_MS
      )
    );
  const derivedRunScope = runProfile === 'full' ? 'live' : 'preview';
  const effectiveRunInputMode = runProfile === 'full' ? 'live_sources' : runInputMode;
  const effectiveCategoryRunMode: TrendCategoryRunMode = derivedRunScope === 'live' ? runCategoryRunMode : 'full';
  const isResetBusy = busyAction === 'resetPreview' || busyAction === 'resetSnapshot' || busyAction === 'hardReset';
  const resetRequiredText = resetPreview?.requiredConfirmText || 'RESET TRENDDATA';
  const selectedTestSet = useMemo(
    () => testSets.find((entry) => entry.key === runFixtureKey) || null,
    [runFixtureKey, testSets],
  );

  useEffect(() => {
    if (runProfile !== 'full' && runInputMode === 'live_sources') {
      setRunInputMode('fixture');
    }
  }, [runInputMode, runProfile]);

  useEffect(() => {
    if (!hasActiveRun && !hasActiveDiscoveryRun) return undefined;
    const intervalId = window.setInterval(() => { void load(); }, 5000);
    return () => window.clearInterval(intervalId);
  }, [hasActiveDiscoveryRun, hasActiveRun, load]);

  useEffect(() => {
    if (runProfile === 'full') return;
    if (effectiveRunInputMode !== 'fixture') return;
    if (runFixtureKey) return;
    if (testSets.length === 0) return;

    const preferred = testSets.find((entry) => entry.recommendedProfile === runProfile) || testSets[0];
    if (preferred) {
      setRunFixtureKey(preferred.key);
    }
  }, [effectiveRunInputMode, runFixtureKey, runProfile, testSets]);

  const previousDiscoveryStatusRef = useRef<TrendDiscoveryRunState['status'] | null>(null);
  useEffect(() => {
    const previousStatus = previousDiscoveryStatusRef.current;
    const nextStatus = discoveryRun?.status || null;

    if (previousStatus === 'running' && nextStatus === 'completed' && discoveryRun?.summary) {
      const needsValidationCount = getDiscoverySummaryNumber(discoveryRun.summary, 'needsValidationCount');
      const inspirationOnlyCount = getDiscoverySummaryNumber(discoveryRun.summary, 'inspirationOnlyCount');
      setMessage(
        `Discovery klar. Kandidater: ${discoveryRun.summary.candidateCount}, redo: ${discoveryRun.summary.pendingCount}, behöver validering: ${needsValidationCount}, inspiration: ${inspirationOnlyCount}, karantän: ${discoveryRun.summary.quarantinedCount}.`,
      );
      setActionError(null);
      void load();
    }

    if (previousStatus === 'running' && nextStatus === 'failed' && discoveryRun?.error) {
      setActionError(discoveryRun.error);
    }

    previousDiscoveryStatusRef.current = nextStatus;
  }, [discoveryRun, load]);

  const openCreateDialog = () => { setDraftSource(EMPTY_SOURCE); setFocusTagsDraft(''); setDialogOpen(true); };
  const openEditDialog = (source: TrendSource) => { setDraftSource(source); setFocusTagsDraft(joinMultiValue(source.focusTags || [])); setDialogOpen(true); };
  const openCreateDiscoveryQueryDialog = () => {
    setDraftDiscoveryQuery(EMPTY_DISCOVERY_QUERY);
    setDiscoveryQueryDialogOpen(true);
  };
  const openEditDiscoveryQueryDialog = (query: TrendDiscoveryQuery) => {
    setDraftDiscoveryQuery(query);
    setDiscoveryQueryDialogOpen(true);
  };

  const handleSaveSource = async () => {
    const payload = { ...draftSource, focusTags: splitMultiValue(focusTagsDraft) };
    setActionError(null); setBusyAction('saveSource');
    try {
      if (draftSource._id) { await updateTrendSource(draftSource._id, payload); setMessage('Källa uppdaterad.'); }
      else { await createTrendSource(payload); setMessage('Källa skapad.'); }
      setDialogOpen(false); await load();
    } catch (err: any) { setActionError(mapFriendlyActionError(err, 'Kunde inte spara källan.')); }
    finally { setBusyAction(null); }
  };

  const handleSaveDiscoveryQuery = async () => {
    const nextLanguage: TrendDiscoveryQuery['language'] = draftDiscoveryQuery.language === 'en' ? 'en' : 'sv';
    const payload = {
      key: String(draftDiscoveryQuery.key || '').trim(),
      label: String(draftDiscoveryQuery.label || '').trim(),
      track: String(draftDiscoveryQuery.track || 'general').trim(),
      category: String(draftDiscoveryQuery.category || 'mixed').trim(),
      language: nextLanguage,
      prompt: String(draftDiscoveryQuery.prompt || '').trim(),
      priority: Number(draftDiscoveryQuery.priority || 0),
      active: draftDiscoveryQuery.active !== false,
    };

    setActionError(null);
    setBusyAction('saveDiscoveryQuery');
    try {
      if (draftDiscoveryQuery._id) {
        await updateTrendDiscoveryQuery(draftDiscoveryQuery._id, payload);
        setMessage('Discovery-fråga uppdaterad.');
      } else {
        await createTrendDiscoveryQuery(payload);
        setMessage('Discovery-fråga skapad.');
      }
      setDiscoveryQueryDialogOpen(false);
      await load();
    } catch (err: any) {
      setActionError(mapFriendlyActionError(err, 'Kunde inte spara discovery-frågan.'));
    } finally {
      setBusyAction(null);
    }
  };

  const handleDeleteSource = async (sourceId: string) => {
    await deleteTrendSource(sourceId); setMessage('Källa borttagen.'); await load();
  };

  const handleRunNow = async () => {
    setActionError(null); setBusyAction('run');
    try {
      const sourceItemIds = runSourceItemIdsDraft
        .split(/[\n,]/)
        .map((entry) => entry.trim())
        .filter(Boolean);
      if (derivedRunScope === 'preview' && effectiveRunInputMode === 'fixture' && !runFixtureKey.trim()) {
        setActionError('Välj testdata innan du startar testkörningen.');
        return;
      }
      if (derivedRunScope === 'preview' && effectiveRunInputMode === 'source_items' && sourceItemIds.length === 0) {
        setActionError('Lägg till minst ett källpost-id innan du startar testkörningen.');
        return;
      }
      const startedRun = await runTrendPipelineNow({
        scope: derivedRunScope,
        profile: runProfile,
        inputMode: effectiveRunInputMode,
        categoryRunMode: effectiveCategoryRunMode,
        fixtureKey: effectiveRunInputMode === 'fixture' ? (runFixtureKey.trim() || null) : null,
        sourceItemIds: effectiveRunInputMode === 'source_items' ? sourceItemIds : [],
      });
      setRuns((current) => [startedRun, ...current.filter((run) => run._id !== startedRun._id)]);
      setMessage(
        derivedRunScope === 'preview'
          ? 'Förhandskörning startad. Ingen livedata skrivs.'
          : `Trendkörning startad i bakgrunden för ${getRunCategoryModeLabel(effectiveCategoryRunMode).toLowerCase()}. Status uppdateras automatiskt.`,
      );
      await load();
    } catch (err: any) { setActionError(mapFriendlyActionError(err, 'Kunde inte starta trendkörningen.')); }
    finally { setBusyAction(null); }
  };

  const buildPilotRunRequest = (preset: TrendPilotRunPreset): TrendRunRequest => {
    const fixtureKey = runFixtureKey.trim()
      || testSets.find((entry) => entry.recommendedProfile === preset.plan.startOptions.profile)?.key
      || testSets[0]?.key
      || '';
    const selectedSourceId = preset.key === 'single_source_test'
      && getSourceTestSources(sources).some((source) => source._id === pilotSourceTestSourceId)
      ? pilotSourceTestSourceId
      : '';
    const selectedDiscoveryQueryId = preset.key === 'single_discovery_query_preview'
      && getDiscoveryQueriesForPreset(preset, discoveryQueries).some((query) => query._id === pilotDiscoveryQueryId)
      ? pilotDiscoveryQueryId
      : '';
    return {
      ...preset.request,
      scope: {
        ...(preset.request.scope || {}),
        ...(preset.key === 'single_source_test' && selectedSourceId ? { sourceIds: [selectedSourceId] } : {}),
        ...(preset.key === 'single_discovery_query_preview' && selectedDiscoveryQueryId ? { discoveryQueryIds: [selectedDiscoveryQueryId] } : {}),
      },
      options: {
        ...(preset.request.options || {}),
        ...(fixtureKey && preset.key !== 'brand_radar_preview' && preset.key !== 'single_discovery_query_preview' && preset.key !== 'report_only_briefing' && preset.key !== 'second_opinion_only' ? { fixtureKey } : {}),
      },
    };
  };

  const getPilotPresetStartBlocker = (preset: TrendPilotRunPreset): string => {
    if (preset.key === 'single_discovery_query_preview') {
      const availableQueries = getDiscoveryQueriesForPreset(preset, discoveryQueries);
      if (availableQueries.length === 0) {
        return 'Aktivera minst en discovery-fråga först.';
      }
      if (!pilotDiscoveryQueryId) {
        return 'Välj vilken discovery-fråga som ska testas.';
      }
      if (!availableQueries.some((query) => query._id === pilotDiscoveryQueryId)) {
        return 'Den valda discovery-frågan är inaktiv eller matchar inte testets spår.';
      }
    }
    if (preset.key === 'single_source_test') {
      const availableSources = getSourceTestSources(sources);
      if (availableSources.length === 0) {
        return 'Skapa eller aktivera minst en källa först.';
      }
      if (!pilotSourceTestSourceId) {
        return 'Välj vilken källa som ska testas.';
      }
      if (!availableSources.some((source) => source._id === pilotSourceTestSourceId)) {
        return 'Den valda källan är inte aktiv längre.';
      }
    }
    return '';
  };

  const handlePlanPilotRun = async (preset: TrendPilotRunPreset) => {
    setActionError(null);
    setBusyAction('pilotPlan');
    try {
      const plan = await planTrendRun(buildPilotRunRequest(preset));
      setPilotPlan(plan);
      setMessage(`Plan klar: ${preset.label}. Kostnadstak cirka ${plan.estimatedCost.max} ${plan.estimatedCost.currency}.`);
    } catch (err: any) {
      setActionError(mapFriendlyActionError(err, 'Kunde inte planera pilotkörningen.'));
    } finally {
      setBusyAction(null);
    }
  };

  const handleStartPilotRun = async (preset: TrendPilotRunPreset) => {
    setActionError(null);
    setBusyAction('pilotStart');
    try {
      const request = buildPilotRunRequest(preset);
      const presetBlocker = getPilotPresetStartBlocker(preset);
      if (presetBlocker) {
        setActionError(presetBlocker);
        return;
      }
      const fixtureKey = typeof request.options?.fixtureKey === 'string' ? request.options.fixtureKey.trim() : '';
      const needsFixtureOrSourceItems = preset.key !== 'single_source_test'
        && preset.key !== 'brand_radar_preview'
        && preset.key !== 'single_discovery_query_preview'
        && preset.key !== 'report_only_briefing'
        && preset.key !== 'second_opinion_only';
      if (needsFixtureOrSourceItems && !fixtureKey && (!request.scope?.sourceItemIds || request.scope.sourceItemIds.length === 0)) {
        setActionError('Välj testdata innan du startar den förvalda testkörningen, eller använd valda källposter.');
        return;
      }
      const result = await startPlannedTrendRun(request);
      setPilotPlan(result.plan);
      setMessage(`${preset.label} startad. Körnings-id: ${result.run._id}`);
      await load();
    } catch (err: any) {
      setActionError(mapFriendlyActionError(err, 'Kunde inte starta pilotkörningen.'));
    } finally {
      setBusyAction(null);
    }
  };

  const toggleSourceTestSource = (sourceId: string) => {
    setSourceTestSourceIds((current) => (
      current.includes(sourceId)
        ? current.filter((entry) => entry !== sourceId)
        : [...current, sourceId]
    ));
  };

  const buildSourceTestRequest = (sourceIds: string[]): TrendRunRequest => ({
    runMode: 'source_test',
    publishMode: 'preview',
    aiProfile: 'preview',
    scope: {
      sourceIds,
    },
    limits: {
      maxSources: Math.max(1, sourceIds.length),
      maxItemsPerSource: 4,
      maxArticlesToAnalyze: Math.max(2, sourceIds.length * 4),
      maxSignalsToVerify: 2,
      maxEstimatedCost: 10,
    },
    phases: {
      discovery: false,
      researchSynthesis: false,
      aggregateSignals: false,
      verifySignals: false,
      matchProducts: false,
      matchRecipes: false,
      computeOpportunities: false,
      generateTrendCards: false,
      generateRecipes: false,
      generateBriefing: false,
      runSecondOpinion: false,
    },
  });

  const handleStartSourceTest = async (sourceIds: string[]) => {
    const selectedIds = sourceIds.filter(Boolean);
    if (selectedIds.length === 0) {
      setActionError('Välj minst en källa för källtest.');
      return;
    }
    setActionError(null);
    setBusyAction('pilotStart');
    try {
      const result = await startPlannedTrendRun(buildSourceTestRequest(selectedIds));
      setPilotPlan(result.plan);
      setMessage(`Källtest startat för ${selectedIds.length} källa/källor. Körnings-id: ${result.run._id}`);
      await load();
    } catch (err: any) {
      setActionError(mapFriendlyActionError(err, 'Kunde inte starta källtestet.'));
    } finally {
      setBusyAction(null);
    }
  };

  const buildBrandRadarRequest = (): TrendRunRequest => ({
    runMode: 'brand_radar',
    publishMode: 'preview',
    aiProfile: 'preview',
    scope: {
      lanes: ['brand_radar'],
      brandRadarQueries: splitMultiValue(brandRadarQueriesDraft).slice(0, 12),
    },
    limits: {
      maxSources: 0,
      maxItemsPerSource: 0,
      maxArticlesToAnalyze: 30,
      maxSignalsToVerify: 0,
      maxEstimatedCost: 8,
    },
  });

  const handleStartBrandRadar = async () => {
    const queries = splitMultiValue(brandRadarQueriesDraft);
    if (queries.length === 0) {
      setActionError('Lägg till minst en Fontana-sökning för Brand Radar.');
      return;
    }
    setActionError(null);
    setBusyAction('brandRadarRun');
    try {
      const result = await startPlannedTrendRun(buildBrandRadarRequest());
      setPilotPlan(result.plan);
      setMessage(`Brand Radar startad. Körnings-id: ${result.run._id}`);
      await load();
    } catch (err: any) {
      setActionError(mapFriendlyActionError(err, 'Kunde inte starta Brand Radar.'));
    } finally {
      setBusyAction(null);
    }
  };

  const handleUpdateBrandRadarHit = async (hitId: string, payload: Partial<Pick<TrendBrandRadarHit, 'isFalsePositive' | 'falsePositiveReason' | 'classification' | 'sentiment' | 'reviewNote'>>) => {
    setActionError(null);
    setBusyAction('brandRadarReview');
    try {
      const updated = await updateBrandRadarHit(hitId, payload);
      setBrandRadarHits((current) => current.map((hit) => (hit._id === hitId ? updated : hit)));
      setMessage('Brand Radar-träffen uppdaterades.');
    } catch (err: any) {
      setActionError(mapFriendlyActionError(err, 'Kunde inte uppdatera Brand Radar-träffen.'));
    } finally {
      setBusyAction(null);
    }
  };

  const handleCancelRun = async () => {
    const runToCancel = activeRun || latestRun;
    if (!runToCancel?._id) return;
    setActionError(null); setBusyAction('cancel');
    try {
      const cancelledRun = await cancelTrendRun(runToCancel._id);
      setMessage(
        cancelledRun.status === 'cancelled'
          ? 'Trendkörningen markerades som avbruten.'
          : 'Avbrott begärt. Status uppdateras automatiskt.',
      );
      await load();
    } catch (err: any) { setActionError(mapFriendlyActionError(err, 'Kunde inte avbryta trendkörningen.')); }
    finally { setBusyAction(null); }
  };

  const handleImportRecommended = async (keys?: string[]) => {
    setActionError(null); setBusyAction('import');
    try {
      const result = await importRecommendedTrendSources(keys);
      setMessage(`Importerade källor. Nya: ${result.createdCount}, överhoppade: ${result.skippedCount}.`);
      await load();
    } catch (err: any) { setActionError(mapFriendlyActionError(err, 'Kunde inte importera rekommenderade källor.')); }
    finally { setBusyAction(null); }
  };

  const handleBackfillImages = async () => {
    setActionError(null); setBusyAction('imageBackfill');
    try {
      const summary = await backfillTrendImages();
      setMessage(
        `Bildåterfyllning klar. Källposter uppdaterade: ${summary.normalizedSourceItems}, nya bildträffar: ${summary.enrichedSourceItems}, artiklar uppdaterade: ${summary.updatedArticles}, berättelser uppdaterade: ${summary.updatedStories}.`,
      );
      await load();
    } catch (err: any) { setActionError(mapFriendlyActionError(err, 'Kunde inte återfylla artikelbilder.')); }
    finally { setBusyAction(null); }
  };

  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [archiveDays, setArchiveDays] = useState(30);

  const handleArchiveOldArticles = async () => {
    setActionError(null); setBusyAction('archive');
    setArchiveConfirmOpen(false);
    try {
      const before = new Date(Date.now() - archiveDays * 86400000).toISOString();
      const result = await archiveTrendArticlesBefore(before);
      setMessage(`${result.archivedCount} artiklar arkiverade (äldre än ${archiveDays} dagar).`);
      await load();
    } catch (err: any) { setActionError(mapFriendlyActionError(err, 'Kunde inte arkivera artiklar.')); }
    finally { setBusyAction(null); }
  };

  const handleRunDiscovery = async () => {
    setActionError(null); setBusyAction('discoveryRun');
    try {
      const nextState = await runTrendDiscoveryNow();
      setDiscoveryRun(nextState);
      setMessage('Discovery startad. Status uppdateras automatiskt medan kandidaterna hämtas.');
      setActiveTab(2);
    } catch (err: any) { setActionError(mapFriendlyActionError(err, 'Kunde inte köra discovery.')); }
    finally { setBusyAction(null); }
  };

  const handleDiscoveryAction = async (
    candidateId: string,
    action: 'approve' | 'reject' | 'quarantine' | 'import' | 'sourceDraft',
  ) => {
    setActionError(null); setBusyAction('discoveryReview');
    try {
      if (action === 'approve') await approveTrendDiscoveryCandidate(candidateId);
      if (action === 'reject') await rejectTrendDiscoveryCandidate(candidateId);
      if (action === 'quarantine') await quarantineTrendDiscoveryCandidate(candidateId);
      if (action === 'import') await importTrendDiscoveryCandidate(candidateId);
      if (action === 'sourceDraft') await createTrendDiscoverySourceDraft(candidateId);
      setMessage(
        action === 'approve'
          ? 'Kandidaten godkänd.'
          : action === 'reject'
            ? 'Kandidaten avvisad.'
            : action === 'quarantine'
              ? 'Kandidaten flyttad till karantän.'
              : action === 'import'
                ? 'Kandidaten importerad till intake.'
                : 'Källdraft skapad.',
      );
      await load();
    } catch (err: any) { setActionError(mapFriendlyActionError(err, 'Kunde inte uppdatera discovery-kandidaten.')); }
    finally { setBusyAction(null); }
  };

  const handleSaveSettings = async () => {
    if (!settings) return;
    if (!canAdmin) {
      setActionError('Bara användare med trends:admin kan spara trendinställningarna.');
      return;
    }
    setActionError(null); setBusyAction('saveSettings');
    try {
      const next = await saveTrendSettings({
        ...settings,
        fontanaContext: {
          ...settings.fontanaContext,
          activeMarkets: splitMultiValue(multiValueDrafts.activeMarkets),
          priorityCategories: splitMultiValue(multiValueDrafts.priorityCategories),
          priorityIngredients: splitMultiValue(multiValueDrafts.priorityIngredients),
          recipeFocus: splitMultiValue(multiValueDrafts.recipeFocus),
          growthGoals: splitMultiValue(multiValueDrafts.growthGoals),
          salesChannels: splitMultiValue(multiValueDrafts.salesChannels),
          dvhFocus: splitMultiValue(multiValueDrafts.dvhFocus),
          foodserviceFocus: splitMultiValue(multiValueDrafts.foodserviceFocus),
          avoidThemes: splitMultiValue(multiValueDrafts.avoidThemes),
        },
        sourceStrategy: {
          ...settings.sourceStrategy,
          prioritizedThemes: splitMultiValue(multiValueDrafts.prioritizedThemes),
          socialPilotTerms: splitMultiValue(multiValueDrafts.socialPilotTerms),
          designFocusAreas: splitMultiValue(multiValueDrafts.designFocusAreas),
          consumerInsightFocus: splitMultiValue(multiValueDrafts.consumerInsightFocus),
        },
      });
      setSettings(next);
      setMultiValueDrafts(buildMultiValueDrafts(next));
      setMessage('Inställningarna sparades.');
    } catch (err: any) { setActionError(mapFriendlyActionError(err, 'Kunde inte spara inställningarna.')); }
    finally { setBusyAction(null); }
  };

  const loadResetPreview = useCallback(async () => {
    if (!canAdmin) {
      setActionError('Bara användare med trends:admin kan använda beta-reset.');
      return null;
    }
    setActionError(null);
    setBusyAction('resetPreview');
    try {
      const preview = await fetchTrendResetPreview();
      setResetPreview(preview);
      return preview;
    } catch (err: any) {
      setActionError(mapFriendlyActionError(err, 'Kunde inte läsa återställningsförhandsvisningen.'));
      return null;
    } finally {
      setBusyAction(null);
    }
  }, [canAdmin]);

  const handleOpenResetDialog = async () => {
    setResetDialogOpen(true);
    setResetSnapshot(null);
    setResetConfirmText('');
    setResetRestartPipeline(false);
    await loadResetPreview();
  };

  const handleCloseResetDialog = () => {
    setResetDialogOpen(false);
    setResetConfirmText('');
    setResetRestartPipeline(false);
    setResetSnapshot(null);
  };

  const handleCreateResetSnapshot = async () => {
    if (!canAdmin) {
      setActionError('Bara användare med trends:admin kan skapa resetsnapshots.');
      return;
    }
    setActionError(null);
    setBusyAction('resetSnapshot');
    try {
      const snapshot = await createTrendResetSnapshot();
      setResetSnapshot(snapshot);
      setResetPreview(snapshot.preview);
      setMessage(`Snapshot skapad: ${snapshot.fileName}.`);
    } catch (err: any) {
      setActionError(mapFriendlyActionError(err, 'Kunde inte skapa resetsnapshot.'));
      await loadResetPreview();
    } finally {
      setBusyAction(null);
    }
  };

  const handleDownloadResetSnapshot = async (snapshotId: string, fileName: string) => {
    setActionError(null);
    try {
      const blob = await downloadTrendResetSnapshot(snapshotId);
      const objectUrl = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (err: any) {
      setActionError(mapFriendlyActionError(err, 'Kunde inte ladda ner återställningssnapshot.'));
    }
  };

  const handleHardReset = async () => {
    if (!canAdmin) {
      setActionError('Bara användare med trends:admin kan köra hård återställning.');
      return;
    }
    if (!resetSnapshot?.snapshotId) {
      setActionError('Skapa först ett snapshot innan du kör hård återställning.');
      return;
    }

    setActionError(null);
    setBusyAction('hardReset');
    try {
      const result = await hardResetTrendData({
        snapshotId: resetSnapshot.snapshotId,
        confirmText: resetConfirmText.trim(),
        restartPipeline: resetRestartPipeline,
      });
      setResetSummary(result);
      setResetDialogOpen(false);
      setResetConfirmText('');
      setResetRestartPipeline(false);
      setResetSnapshot(null);
      setResetPreview(null);
      if (result.restartError) {
        setActionError(result.restartError);
      } else {
        setActionError(null);
      }
      setMessage(
        result.restartedRun
          ? 'Trenddata rensades och en ny trendkörning startades.'
          : 'Trenddata rensades. Nästa körning blir ny baseline.',
      );
      await load();
    } catch (err: any) {
      setActionError(mapFriendlyActionError(err, 'Kunde inte hard-resetta trenddata.'));
      await loadResetPreview();
    } finally {
      setBusyAction(null);
    }
  };

  const activeSourceCountLabel = useMemo(() => `${sources.length} aktiva eller sparade`, [sources.length]);
  const pendingDiscoveryCount = useMemo(
    () => discoveryCandidates.filter((candidate) => (
      candidate.status === 'pending'
      || candidate.status === 'needs_validation'
      || candidate.status === 'inspiration_only'
    )).length,
    [discoveryCandidates],
  );
  const importedDiscoveryCount = useMemo(
    () => discoveryCandidates.filter((candidate) => candidate.status === 'imported').length,
    [discoveryCandidates],
  );
  const sortedDiscoveryQueries = useMemo(
    () => [...discoveryQueries].sort((left, right) => (
      (Number(right.priority || 0) - Number(left.priority || 0))
      || String(left.label || '').localeCompare(String(right.label || ''))
    )),
    [discoveryQueries],
  );
  const activeDiscoveryQueries = useMemo(
    () => sortedDiscoveryQueries.filter((query) => query.active),
    [sortedDiscoveryQueries],
  );
  const latestDiscoveryCoverage = discoveryRun?.summary?.coverage || activeRun?.discoveryCoverage || latestRun?.discoveryCoverage || null;
  const discoveryContract = latestDiscoveryCoverage?.contract || DISCOVERY_CONTRACT_FALLBACK;

  return {
    canAdmin,
    canReview,
    canManageSources,
    navigateToView,
    sources,
    setSources,
    recommendedSources,
    setRecommendedSources,
    testSets,
    setTestSets,
    pilotPresets,
    setPilotPresets,
    pilotPlan,
    setPilotPlan,
    pilotPresetError,
    setPilotPresetError,
    discoveryCandidates,
    setDiscoveryCandidates,
    discoveryQueries,
    setDiscoveryQueries,
    brandRadarHits,
    setBrandRadarHits,
    brandRadarQueriesDraft,
    setBrandRadarQueriesDraft,
    showBrandRadarFalsePositives,
    setShowBrandRadarFalsePositives,
    settings,
    setSettings,
    multiValueDrafts,
    setMultiValueDrafts,
    runs,
    setRuns,
    discoveryRun,
    setDiscoveryRun,
    error,
    setError,
    testSetsError,
    setTestSetsError,
    actionError,
    setActionError,
    recommendedError,
    setRecommendedError,
    message,
    setMessage,
    dialogOpen,
    setDialogOpen,
    discoveryQueryDialogOpen,
    setDiscoveryQueryDialogOpen,
    draftSource,
    setDraftSource,
    draftDiscoveryQuery,
    setDraftDiscoveryQuery,
    focusTagsDraft,
    setFocusTagsDraft,
    busyAction,
    setBusyAction,
    activeTab,
    setActiveTab,
    runProfile,
    setRunProfile,
    runInputMode,
    setRunInputMode,
    runCategoryRunMode,
    setRunCategoryRunMode,
    runFixtureKey,
    setRunFixtureKey,
    runSourceItemIdsDraft,
    setRunSourceItemIdsDraft,
    sourceTestSourceIds,
    setSourceTestSourceIds,
    pilotSourceTestSourceId,
    setPilotSourceTestSourceId,
    pilotDiscoveryQueryId,
    setPilotDiscoveryQueryId,
    resetDialogOpen,
    setResetDialogOpen,
    resetPreview,
    setResetPreview,
    resetSnapshot,
    setResetSnapshot,
    resetConfirmText,
    setResetConfirmText,
    resetRestartPipeline,
    setResetRestartPipeline,
    resetSummary,
    setResetSummary,
    archiveConfirmOpen,
    setArchiveConfirmOpen,
    archiveDays,
    setArchiveDays,
    load,
    activeRun,
    visibleRun,
    latestRun,
    hasActiveRun,
    hasActiveDiscoveryRun,
    latestRunUpdatedLabel,
    latestRunHeartbeatLabel,
    latestRunPhaseStartedLabel,
    latestRunAgeMs,
    latestRunHeartbeatAgeMs,
    latestRunUsesLegacyTracking,
    latestRunHeartbeatStale,
    latestRunSlowButAlive,
    latestRunLooksStuck,
    derivedRunScope,
    effectiveRunInputMode,
    effectiveCategoryRunMode,
    isResetBusy,
    resetRequiredText,
    selectedTestSet,
    activeSourceCountLabel,
    pendingDiscoveryCount,
    importedDiscoveryCount,
    sortedDiscoveryQueries,
    activeDiscoveryQueries,
    latestDiscoveryCoverage,
    discoveryContract,
    loadResetPreview,
    openCreateDialog,
    openEditDialog,
    openCreateDiscoveryQueryDialog,
    openEditDiscoveryQueryDialog,
    handleSaveSource,
    handleSaveDiscoveryQuery,
    handleDeleteSource,
    handleRunNow,
    handlePlanPilotRun,
    handleStartPilotRun,
    getPilotPresetStartBlocker,
    toggleSourceTestSource,
    handleStartSourceTest,
    handleStartBrandRadar,
    handleUpdateBrandRadarHit,
    handleCancelRun,
    handleImportRecommended,
    handleBackfillImages,
    handleArchiveOldArticles,
    handleRunDiscovery,
    handleDiscoveryAction,
    handleSaveSettings,
    handleOpenResetDialog,
    handleCloseResetDialog,
    handleCreateResetSnapshot,
    handleDownloadResetSnapshot,
    handleHardReset,
  };
}
